/**
 * HVP API Client - endpoint wrappers
 *
 * One method per admin-ajax action (hvp_cascade_options, hvp_cascade_batch,
 * hvp_match, hvp_find_variation, hvp_health, hvp_check_compatibility).
 * Action names are a server contract - do not rename.
 *
 * Split out of api-client.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPApiClient.prototype by
 * api-client.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const ApiEndpointMethods = {
    /**
     * Cascade options request
     *
     * @param {number} productId - Product ID
     * @param {Object} selections - Current selections
     * @param {string} attribute - Target attribute
     * @returns {Promise<Object>} Cascade result
     */
    async cascade(productId, selections, attribute) {
        return this.request('hvp_cascade_options', {
            product_id: productId,
            selections: selections,
            attribute: attribute
        });
    },

    /**
     * Batch cascade request (multiple attributes)
     *
     * @param {number} productId - Product ID
     * @param {Object} selections - Current selections
     * @param {string[]} attributeOrder - Attribute order
     * @returns {Promise<Object>} Batch cascade result
     */
    async cascadeBatch(productId, selections, attributeOrder) {
        return this.request('hvp_cascade_batch', {
            product_id: productId,
            selections: selections,
            attribute_order: attributeOrder
        });
    },

    /**
     * Match variation request
     *
     * @param {number} productId - Product ID
     * @param {Object} selections - Current selections
     * @returns {Promise<Object>} Matching variation
     */
    async match(productId, selections) {
        return this.request('hvp_match', {
            product_id: productId,
            selections: selections
        });
    },

    /**
     * Authoritative server-side variation lookup (single attempt).
     *
     * Used by the no-match re-check. The happy path resolves client-side from
     * the page-baked dataset, which for anonymous visitors can be a stale
     * FlyingPress-cached blob (admins bypass the cache). This asks the server
     * once via the legacy hvp_find_variation handler; its response shape is
     * { found, variation, query_time_ms, data_version }. maxRetries:1 keeps the
     * dead-end path snappy (no exponential backoff on a confirmed miss).
     *
     * @param {number} productId - Product ID
     * @param {Object} selections - Current selections
     * @returns {Promise<Object>} { found, variation, data_version, query_time_ms }
     */
    async findVariation(productId, selections) {
        return this.request('hvp_find_variation', {
            product_id: productId,
            selections: selections
        }, { maxRetries: 1 });
    },

    /**
     * Health check request
     *
     * @param {number} productId - Product ID
     * @returns {Promise<Object>} Health status
     */
    async health(productId) {
        return this.request('hvp_health', {
            product_id: productId
        });
    },

    /**
     * Check compatibility of current selections
     *
     * Validates whether the complete set of selections (including values
     * above and below the changed index) form a valid variation combination.
     * Used by Smart Cascade to preserve below-selections when possible.
     *
     * @param {number} productId - Product ID
     * @param {Object} selections - Complete selections object to validate
     * @returns {Promise<Object>} Compatibility result
     * @returns {boolean} result.isCompatible - Whether selections form valid combination
     * @returns {Object|null} result.variation - Matching variation if compatible
     * @returns {string} [result.error] - Error message if request failed
     *
     * @example
     * const result = await api.checkCompatibility(123, {
     *   pa_size: 'large',
     *   pa_color: 'red',
     *   pa_material: 'cotton'
     * });
     * if (result.isCompatible) {
     *   window.hvpData && window.hvpData.debug && console.log('Valid combination:', result.variation.variation_id);
     * }
     */
    async checkCompatibility(productId, selections) {
        return this.request('hvp_check_compatibility', {
            product_id: productId,
            selections: selections
        });
    },

    /**
     * Load options filtered by partial selections (above only)
     *
     * Used when focusing on a dropdown to load all valid options
     * based only on selections ABOVE this attribute in the cascade.
     * This enables the Smart Cascade "peek ahead" functionality.
     *
     * @param {number} productId - Product ID
     * @param {Object} selectionsAbove - Selections from attributes above current
     * @param {string} attribute - Target attribute to load options for
     * @returns {Promise<Object>} Options result
     * @returns {Array} result.options - Available options for the attribute
     *
     * @example
     * // User focuses on pa_color (index 1), load options based on pa_size only
     * const result = await api.loadFilteredOptions(123, { pa_size: 'large' }, 'pa_color');
     */
    async loadFilteredOptions(productId, selectionsAbove, attribute) {
        return this.request('hvp_cascade_options', {
            product_id: productId,
            selections: selectionsAbove,
            attribute: attribute,
            filter_mode: 'above_only'
        });
    },
};

export default ApiEndpointMethods;
